"use strict";

const expressDom = require("./expressDom");

/**
 * DGF-4617 — the DIRECT PayPal express wallet (int_paypal / int_paypal_overlay), rendered through
 * the same walletRenderer pipeline as the Adyen wallets so the three express surfaces stay one
 * system: one container, one allow-list, one busy/error/empty state machine, one button geometry.
 *
 * This is a LOCAL wallet (walletGating.LOCAL_WALLETS): Adyen's /paymentMethods response knows
 * nothing about it, its availability comes from PayPal's own SDK (`Buttons(...).isEligible()`),
 * and the server only lists it on a container (data-express-local-wallets) when the surface's
 * sdPayPalExpress_* site preference is on.
 *
 * The payment flow is the direct-PayPal express chain, not Adyen's:
 *   createOrder  -> Paypal-GetPaypalOrderId ({isExpress:true} adds shipping options to the
 *                   purchase unit; on the PDP the product is added to the basket first)
 *   onApprove    -> Paypal-ReturnFromCart   (writes PayPal's address/email, selects the shipping
 *                   option the shopper chose in the sheet)
 *                -> CheckoutServices-PlaceOrder (paypal Authorize hook captures)
 *                -> POST orderID/orderToken to the continueUrl (Order-Confirm)
 *
 * Deliberately dependency-injected and free of jQuery and cartridge aliases, like every module in
 * this folder, so it is unit-testable under jest's node environment.
 */

const USED_PAYMENT_METHOD = "PayPal";

/**
 * Pinned SDK button style for the express surfaces, applied over whatever the legacy
 * paypal*ButtonConfig preference carries. The express row must read as one set of buttons next to
 * Apple Pay and Google Pay, so the parts of the legacy style that describe a different surface are
 * fixed here: PayPal white, rectangular rather than the pill
 * shape, no tagline (it renders a second text line below the button), and the shared express
 * height — without it the SDK's ~45px default floats inside the 54px slot, visibly so next to the
 * sticky bar's CTA.
 * $express-button-height in _expressPayments.scss is the same 54; PayPal's SDK caps height at 55.
 *
 * NB: while this was black the iframe carried a `filter: contrast(3)` to force PayPal's off-black
 * to a true #000. That filter is gone with the colour — it would visibly distort the button.
 */
const EXPRESS_BUTTON_STYLE = {
    color: "white",
    // The mark alone — the legacy pref's "checkout" label renders an animated "PayPal Checkout"
    // wordmark, and the Adyen siblings show only their marks (user direction).
    label: "paypal",
    shape: "rect",
    // The rounding is drawn INSIDE PayPal's iframe, where page CSS cannot reach; borderRadius is
    // the SDK's own escape hatch. NOT 0: the SDK validates 0 as legal but its CSS generator
    // guards with a truthiness check — `(i && isNumber(i) ? "border-radius: "+i+"px" : "")` — so 0
    // is silently dropped and the rebranded pill radius (height/2) survives. 0.1 survives the
    // guard and 0.1px is sub-pixel at any zoom, unlike 1px which showed when zoomed in.
    borderRadius: 0.1,
    tagline: false,
    height: 54
};

/**
 * Reads and parses a JSON data attribute off an element. Malformed JSON reads as null — a payment
 * button must not throw during render because of a bad attribute.
 *
 * @param {Object} element - element with a getAttribute method
 * @param {string} attributeName - attribute to read
 * @returns {Object|null} the parsed value
 */
function readJsonAttribute(element, attributeName) {
    if (!element || typeof element.getAttribute !== "function") {
        return null;
    }
    const raw = element.getAttribute(attributeName);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw);
    } catch (error) {
        return null;
    }
}

/**
 * The promoted express CSRF token (expressDom.promoteTokenElement runs before any render pass).
 *
 * @param {Object} doc - document-like object
 * @returns {Object|null} { name, value } or null
 */
function readCsrfToken(doc) {
    const tokenElement = doc.getElementById && doc.getElementById("adyen-token");
    if (!tokenElement) {
        return null;
    }
    const name = tokenElement.getAttribute("name");
    const value = tokenElement.getAttribute("value") || tokenElement.value;
    return name && value ? { name: name, value: value } : null;
}

/**
 * Appends one query parameter, choosing the separator from the URL rather than assuming one.
 *
 * Every append here is conditional — the CSRF token and the flow id may both be absent — so a
 * hard-coded `&` on a later parameter produces `Route&name=value`, a URL with no query string at
 * all. Unreachable today (each render path is gated on the token element existing), but the
 * helpers permit it, so the separator is derived every time.
 *
 * @param {string} url - route URL
 * @param {string} name - parameter name
 * @param {string} value - parameter value, appended URL-encoded
 * @returns {string} the URL with the parameter attached
 */
function withParam(url, name, value) {
    const separator = url.indexOf("?") > -1 ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
}

/**
 * Appends the CSRF token to a URL — the Paypal-* express routes validate it.
 *
 * @param {string} url - route URL
 * @param {Object|null} csrf - { name, value }
 * @returns {string} the URL with the token attached
 */
function withCsrf(url, csrf) {
    if (!csrf) {
        return url;
    }
    const separator = url.indexOf("?") > -1 ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(csrf.name)}=${encodeURIComponent(csrf.value)}`;
}

/**
 * Appends the flow id, so the server can prove the request belongs to the basket this button
 * created rather than one another tab has since replaced.
 *
 * @param {string} url - route URL
 * @param {Object} context - the component context
 * @returns {string} the URL with the flow id attached, unchanged when there is no live flow
 */
function withFlow(url, context) {
    if (!context.flowId) {
        return url;
    }
    const separator = url.indexOf("?") > -1 ? "&" : "?";
    return `${url}${separator}flowId=${encodeURIComponent(context.flowId)}`;
}

/**
 * Loads the PayPal JS SDK once. window.paypalSDK (footer remote include) carries the built URLs;
 * the cart flavour is the express one (commit/user_action handled server-side on order creation).
 * Guarded on window.paypal so a second injection cannot race the first — same discipline as
 * int_paypal's own injectPaypalSDK.
 *
 * @param {Object} deps - injected dependencies ({ win, doc })
 * @returns {Promise<boolean>} whether window.paypal is usable
 */
function ensureSdk(deps) {
    const win = deps.win;
    const doc = deps.doc;
    if (!win) {
        return Promise.resolve(false);
    }
    if (win.paypal && typeof win.paypal.Buttons === "function") {
        return Promise.resolve(true);
    }
    const sdkUrl = win.paypalSDK && win.paypalSDK.cartSdkUrl;
    if (!sdkUrl || !doc || typeof doc.createElement !== "function") {
        return Promise.resolve(false);
    }
    if (!win.sdPayPalExpressSdkPromise) {
        win.sdPayPalExpressSdkPromise = new Promise(function loadSdk(resolve) {
            const isReady = function isReady() {
                return Boolean(win.paypal && typeof win.paypal.Buttons === "function");
            };

            // int_paypal's own loader (cart.js injectPaypalSDK) may already have this exact
            // script in flight — window.paypal is still undefined while it loads. Injecting a
            // second copy is the double-load int_paypal's guard comment warns destroys mounted
            // PayPal components, so wait on the existing tag instead of adding another.
            /*
             * REVIEW FINDING — a tag that has already been waited out must not be waited on again.
             *
             * Clearing the memoised promise on failure lets a later pass retry, and removing OUR
             * own dead tag lets that retry inject a fresh one. Neither helps when the dead tag
             * belongs to int_paypal's loader: it stays in the DOM (removing another cartridge's
             * tag is not ours to do, and its own load may still be in flight), so every retry
             * found it and repeated the same 15-second poll, never recovering.
             *
             * So a poll that times out records the URL it gave up on, and later attempts stop
             * treating that tag as something to wait for and inject their own instead. Recorded on
             * `win` rather than in module scope for the same reason the promise is: two surfaces on
             * one page share it.
             */
            const abandoned = win.sdPayPalExpressSdkAbandoned === sdkUrl;
            const existing = abandoned ? null : Array.prototype.filter.call(
                (typeof doc.querySelectorAll === "function" && doc.querySelectorAll("script")) || [],
                function sameSdk(tag) {
                    return tag.getAttribute && tag.getAttribute("src") === sdkUrl;
                }
            )[0];
            if (existing) {
                let waitedMs = 0;
                const poll = function poll() {
                    if (isReady()) {
                        resolve(true);
                        return;
                    }
                    if (waitedMs >= 15000) {
                        // Do not poll this tag again — see above.
                        win.sdPayPalExpressSdkAbandoned = sdkUrl;
                        resolve(false);
                        return;
                    }
                    waitedMs += 200;
                    setTimeout(poll, 200);
                };
                poll();
                return;
            }

            const script = doc.createElement("script");
            script.setAttribute("src", sdkUrl);
            // PayPal's partner revenue attribution (BN code) — int_paypal's loader sets the
            // same attribute; a transaction through an SDK loaded without it loses attribution.
            if (win.paypalPreferences && win.paypalPreferences.partnerAttributionId) {
                script.setAttribute("data-partner-attribution-id", win.paypalPreferences.partnerAttributionId);
            }
            script.onload = function onLoad() {
                resolve(isReady());
            };
            script.onerror = function onError() {
                /*
                 * Remove OUR failed tag before resolving. Clearing the memoised promise below is
                 * not enough on its own: the tag stays in the DOM with this exact `src`, so the
                 * retry takes the "int_paypal already injected this" branch above and polls a dead
                 * tag for 15 seconds before giving up again. Only the tag this closure created is
                 * removed — never int_paypal's, whose own load may still be in flight.
                 */
                if (script.parentNode && typeof script.parentNode.removeChild === "function") {
                    script.parentNode.removeChild(script);
                }
                resolve(false);
            };
            (doc.head || doc.body || doc.documentElement).appendChild(script);
        });
    }
    /*
     * REVIEW FINDING — a FAILED load must not be cached for the life of the page.
     *
     * The promise is memoised so two surfaces cannot inject the SDK twice, which is the right
     * default while it is loading or once it has succeeded. But a resolve(false) — a network blip,
     * an adblocker, a blocked script, the 15s poll giving up — used to be memoised just as
     * permanently, so no later basket refresh or variant switch could ever recover: the buttons
     * stayed absent until the shopper reloaded.
     *
     * Clearing the slot on failure makes the NEXT call retry, while a success stays cached. The
     * result is still returned to this caller, so nothing retries synchronously.
     */
    /*
     * The slot is cleared only if it still holds THIS attempt's promise. Without the identity check
     * a late-resolving failure could null a slot a newer retry had already populated, costing an
     * extra injection — harmless, but it made "exactly one load in flight" a hope rather than a
     * guarantee, which is not what a reader should have to work out.
     */
    const attempt = win.sdPayPalExpressSdkPromise;

    return attempt.then(function forgetFailure(loaded) {
        if (!loaded && win.sdPayPalExpressSdkPromise === attempt) {
            win.sdPayPalExpressSdkPromise = null;
        }
        return loaded;
    });
}

/**
 * Builds the urlencoded body Paypal-ReturnFromCart expects: the serialised billing form fields the
 * server rendered onto the container, with the used payment method forced to PayPal.
 *
 * @param {Object} billingFormFields - { fieldKey: { name, value } | scalar } map
 * @returns {URLSearchParams} the request body
 */
function buildReturnBody(billingFormFields) {
    const body = new URLSearchParams();
    Object.keys(billingFormFields || {}).forEach(function appendField(key) {
        const field = billingFormFields[key];
        if (field && typeof field === "object" && field.name) {
            const value = key === "usedPaymentMethod" ? USED_PAYMENT_METHOD : (field.value || "");
            body.append(field.name, value);
        }
    });
    return body;
}

/**
 * Reports a failed express order — in place on the PDP, by redirect elsewhere.
 *
 * REVIEW FINDING: following `redirectUrl` is silent on the PDP. Every Paypal-* failure hands back
 * `Checkout-Begin?stage=customer&expressPlaceOrderError=<msg>`, but PDP express deliberately leaves
 * the real basket untouched — so for the common case, a shopper whose bag is empty, Checkout-Begin
 * hits its first guard (`if (!currentBasket) res.redirect(Cart-Show)`), the querystring is dropped
 * on that redirect, and `checkout.js`'s `.error-message-text` renderer never runs. The shopper
 * approves the payment and lands on an empty bag with no message, no product and no order.
 * app_superdry's Checkout.js adds a second bail-out when no applicable payment method is found,
 * which an empty basket also triggers, so both routes out lose the message.
 *
 * The PDP already has its own `.js-adyen-express-error` element (product/expressPayments.isml), so
 * the fix is to use it: show the server's own message and stay on the page, where the button the
 * shopper pressed still is. The bag and mini-bag surfaces keep the redirect — they have a real
 * basket, so Checkout-Begin renders and shows the message as intended.
 *
 * @param {Object} context - the component context
 * @param {Object} result - the failing response body
 * @returns {Promise<boolean>} whether the failure was reported (false leaves it to the caller)
 */
async function reportOrderFailure(context, result) {
    const message = (Array.isArray(result && result.errorMessage)
        ? result.errorMessage.join(", ")
        : (result && result.errorMessage)) || context.deps.errorMessage || "";

    if (context.temporaryBasket) {
        /*
         * MESSAGE FIRST, discard second. REVIEW FINDING: awaiting the discard before reporting
         * meant a hung request — dead mobile connection, captive portal, a stalled app server —
         * left the spinner turning with no message, which is the exact silence this function
         * exists to end. `discardTemporaryBasket` is a bare fetch with no AbortController and no
         * timeout, so there is nothing bounding that wait.
         *
         * Nothing depends on the old order here: this path does NOT navigate (that is the point of
         * it), so unlike `navigateAfterCleanup` there is no risk of the discard being cancelled by
         * the page going away. The basket is released either way, and the message is the part the
         * shopper needs.
         */
        expressDom.setSurfaceBusy(context.container, false);
        expressDom.setSurfaceError(context.container, message);
        await discardTemporaryBasket(context);
        return true;
    }

    if (result && result.redirectUrl) {
        // Cleanup BEFORE navigating — setting location.href cancels in-flight requests.
        await navigateAfterCleanup(context, result.redirectUrl);
        return true;
    }

    return false;
}

/**
 * Places the order once PayPal has approved, then hands the browser to the confirmation page.
 *
 * @param {Object} context - { deps, container, urls, csrf, billingFormFields }
 * @returns {Promise<void>} resolves when navigation is under way or the error is on the surface
 */
async function completeOrder(context) {
    const deps = context.deps;
    const container = context.container;
    expressDom.setSurfaceBusy(container, true);
    try {
        /*
         * isExpressWallet marks this as a WALLET return, the same distinction GetPaypalOrderId
         * already makes. ReturnFromCart is shared with the checkout-page PayPal button, whose
         * basket can carry a shopper-CHOSEN method (Click&Collect picked at the shipping step)
         * that must never be silently rewritten — so the server re-asserts the home-delivery-only
         * rule for wallet returns only.
         *
         * It has to be re-asserted there rather than trusted from order creation:
         * addressHelper.updateShippingAddress selects a method of its own when PayPal's address
         * lands, and this site's shippingMethodsOrdering puts pickup-dropoff FIRST — so a
         * Packstation method becomes the basket's method, and reaches the order, whenever PayPal
         * echoes no shipping options back for the chosen-option override to correct.
         */
        const returnUrl = withParam(withFlow(withCsrf(context.urls.returnFromCart, context.csrf), context), "isExpressWallet", "true");
        const returnResponse = await deps.win.fetch(returnUrl, {
            method: "POST",
            body: buildReturnBody(context.billingFormFields)
        });
        const returnResult = await returnResponse.json().catch(function empty() {
            return {};
        });
        if (returnResult && returnResult.csrfError) {
            // SFCC's CSRF middleware responds 200 {csrfError:true} and invalidates the session —
            // proceeding to PlaceOrder would fail against a logged-out, unreconciled basket.
            if (returnResult.redirectUrl) {
                await navigateAfterCleanup(context, returnResult.redirectUrl);
                return;
            }
            throw new Error("your session has expired");
        }
        /*
         * DGF-4651 — a rejected address is fixed IN the PayPal sheet on this page: message under
         * the buttons, no navigation, temporary basket discarded so retry is not blocked.
         */
        if (returnResult && returnResult.expressAddressError) {
            await discardTemporaryBasket(context);
            // Busy cleared EXPLICITLY, not left to setSurfaceError: that only clears it when it
            // has a message to show, so an empty message would leave the buttons spinning for
            // good — the one outcome worse than a generic message.
            expressDom.setSurfaceBusy(container, false);
            expressDom.setSurfaceError(container, returnResult.errorMessage || deps.errorMessage || "");
            return;
        }
        if (!returnResponse.ok || (returnResult && returnResult.error)) {
            // The Paypal-* routes prepare a checkout error page (and stage the real message as a
            // basket flash attribute) whenever they fail — follow it rather than flattening the
            // failure into the generic surface error. A non-ok status with no parseable JSON body
            // (an HTML error page, caught above to {}) still needs to fail here rather than read
            // as a success with no `error` key.
            // In place on the PDP, by redirect on the bag surfaces — see reportOrderFailure.
            if (await reportOrderFailure(context, returnResult)) {
                return;
            }
            throw new Error(
                Array.isArray(returnResult && returnResult.errorMessage)
                    ? returnResult.errorMessage.join(", ")
                    : ((returnResult && returnResult.errorMessage) || "express return failed")
            );
        }

        /*
         * The token is required. On the bag and mini bag this posts to
         * CheckoutServices-PlaceOrder, which carries only server.middleware.https and so never
         * needed one — but the PDP posts to Paypal-PlaceExpressOrder, which validates CSRF like
         * every other Paypal-* express route. Sending it unconditionally is right either way: an
         * order-placing endpoint should check, and the surfaces that do not are the anomaly.
         *
         * Missing it fails as a redirect to CSRF-Fail, which the JSON guard below reads as an
         * unparseable response rather than anything to do with a token — so it presents as a
         * generic express failure after a correctly-priced PayPal sheet.
         */
        const placeResponse = await deps.win.fetch(withFlow(withCsrf(context.urls.placeOrder, context.csrf), context), { method: "POST" });
        // Same guard as the return fetch above: an HTML error page's json() must not throw
        // opaquely and abort the catch's error handling before the redirect/message is read.
        const placeResult = await placeResponse.json().catch(function empty() {
            return {};
        });
        if (!placeResponse.ok || placeResult.error) {
            if (await reportOrderFailure(context, placeResult)) {
                return;
            }
            throw new Error(placeResult.errorMessage || "order could not be placed");
        }

        // A 200 status is not proof PlaceOrder actually ran: a followed redirect to an
        // unrelated page (a login screen, a CDN error page) is also `ok`, and the .json().catch
        // guard above turns its unparseable body into {} — which slips past both checks above
        // with no `error` key. Without continueUrl the confirmation form below would be built
        // with an undefined action and POST to the current page instead of showing an error, so
        // treat a 200 with no usable body as a failed placement rather than a placed order.
        if (!placeResult.continueUrl || !placeResult.orderID || !placeResult.orderToken) {
            throw new Error(placeResult.errorMessage || "order could not be placed");
        }

        const doc = deps.doc;
        const form = doc.createElement("form");
        form.setAttribute("method", "POST");
        form.setAttribute("action", placeResult.continueUrl);
        [["orderID", placeResult.orderID], ["orderToken", placeResult.orderToken]]
            .forEach(function appendInput(pair) {
                const input = doc.createElement("input");
                input.setAttribute("type", "hidden");
                input.setAttribute("name", pair[0]);
                input.setAttribute("value", pair[1]);
                form.appendChild(input);
            });
        doc.body.appendChild(form);
        form.submit();
    } catch (error) {
        // Busy cleared explicitly: setSurfaceError only clears it when it has a message to show,
        // so an empty data-express-error-message would leave the spinner turning for good.
        expressDom.setSurfaceBusy(container, false);
        expressDom.setSurfaceError(container, deps.errorMessage || "");
        if (deps.win && deps.win.console) {
            deps.win.console.error("[express:paypal] order completion failed:", error);
        }
        /*
         * Release the temporary basket HERE. This catch swallows the failure so the SDK's onError
         * never fires, which is where the discard used to live — so a failed return or placement
         * left the basket held and, worse, the session marker set. A marker that outlives the flow
         * makes every later PayPal request resolve a basket the shopper never chose, including the
         * checkout-page button if they carry on to checkout.
         */
        await discardTemporaryBasket(context);
    }
}

/**
 * Releases the temporary basket and THEN navigates.
 *
 * Order matters: setting location.href cancels in-flight requests, so a discard fired after it may
 * never reach the server. Every express failure that redirects goes through here.
 *
 * @param {Object} context - the component context
 * @param {string} url - where to send the shopper
 * @returns {Promise<void>} resolves once navigation has been started
 */
async function navigateAfterCleanup(context, url) {
    await discardTemporaryBasket(context);
    context.deps.win.location.href = url;
}

/**
 * PDP only: puts the selected product into a TEMPORARY basket, so express pay buys that product
 * alone and the shopper's real bag is never touched.
 *
 * Calls Adyen's `Adyen-CreateTemporaryBasket` deliberately rather than adding a second
 * temporary-basket mechanism. That endpoint is Adyen-BRANDED but integration-agnostic — it makes no
 * Adyen API call and returns no Adyen-specific data; the useful part is a side effect, storing the
 * new basket's UUID in `session.privacy.temporaryBasketId`, which the server then resolves through
 * `expressBasket.resolveExpressBasket()`. Apple Pay and Google Pay already work exactly this way,
 * so all three wallets now share one basket mechanism instead of two.
 *
 * It also reads `bundledProducts` and `options`, which the previous bare {pid, quantity} POST to
 * Cart-AddProduct could not carry — which is why direct PayPal used to be withheld on bundles,
 * product sets and option products.
 *
 * The payload comes from #selected-express-product, the hidden input expressDom keeps in step with
 * variant selection (upsertExpressProductForm) and which the vendor's own client reads the same way
 * (app_adyen_SFRA/.../commons/index.js:51-56).
 *
 * @param {Object} context - { deps, container, urls, csrf }
 * @returns {Promise<void>} resolves once the temporary basket holds the product
 */
async function createTemporaryBasketForPdp(context) {
    const doc = context.deps.doc;
    const url = context.urls.createTemporaryBasket;

    if (!url) {
        throw new Error("no createTemporaryBasket URL for PDP express");
    }

    const productInput = doc.getElementById && doc.getElementById("selected-express-product");
    const rawProduct = productInput && (productInput.value || productInput.getAttribute("value"));

    if (!rawProduct) {
        throw new Error("no product payload for PDP express");
    }

    let product;
    try {
        product = JSON.parse(rawProduct);
    } catch (error) {
        throw new Error("unreadable product payload for PDP express");
    }

    if (!product || !product.id) {
        throw new Error("no product to add for PDP express");
    }

    const body = new URLSearchParams();
    // Exactly the four fields createTemporaryBasket destructures out of req.form.data.
    body.append("data", JSON.stringify({
        id: product.id,
        bundledProducts: product.bundledProducts,
        options: product.options,
        selectedQuantity: product.selectedQuantity || 1
    }));

    const response = await context.deps.win.fetch(withCsrf(url, context.csrf), {
        method: "POST",
        body: body
    });
    const result = await response.json();

    if (!response.ok || !result || result.error || !result.temporaryBasketCreated || !result.flowId) {
        throw new Error((result && result.errorMessage) || "could not create the temporary basket");
    }

    /*
     * The flow id identifies THIS button's basket. The session holds one temporaryBasketId, shared
     * with the Adyen wallets, and creating a basket deletes every existing one — so a second tab
     * takes over the marker and destroys this basket. Sending the id back on every later call lets
     * the server refuse to act once it no longer matches, so a stale flow fails instead of pricing,
     * placing or discarding another flow's basket.
     */
    context.flowId = result.flowId;

    /*
     * No add to undo any more, so no marker to keep. The endpoint deletes any basket it left behind
     * on its own next call, and a retry after a closed sheet simply creates a fresh one — which is
     * why the old `addedPid` guard against double-adding is gone with it: there is no shared basket
     * for a second unit to land in.
     */
    context.temporaryBasket = true;
}

/**
 * Discards the temporary basket when a PDP express flow ends without an order.
 *
 * Replaces the old best-effort undo of a real-basket add, which no longer exists: nothing was added
 * to the shopper's bag, so there is nothing to put back. This only asks the server to drop the
 * temporary basket so it does not count against SFCC's per-customer limit, and clears the session
 * marker so the next request resolves the real basket again.
 *
 * Best-effort by design: a failure here leaves an orphaned temporary basket, which the create
 * endpoint clears on its own next call, and which the shopper never sees either way.
 *
 * A FAILED express attempt therefore leaves the shopper with no order AND nothing in their bag —
 * the old real-basket flow left the item behind, so this reads like a regression and is not.
 * Confirmed with the business: it matches Apple Pay and Google Pay, which have always used a
 * temporary basket and have always behaved this way. Do not "restore" the item on failure without
 * revisiting that decision — silently modifying the bag after a failed payment is its own
 * surprise, and it would put PayPal back out of step with the other two wallets.
 *
 * @param {Object} context - the component context
 * @returns {Promise<void>} resolves once the discard has been attempted
 */
async function discardTemporaryBasket(context) {
    if (!context.temporaryBasket || !context.urls.discardTemporaryBasket) {
        return;
    }

    const win = context.deps.win;
    try {
        const response = await win.fetch(withFlow(withCsrf(context.urls.discardTemporaryBasket, context.csrf), context), { method: "POST" });
        const result = await response.json().catch(function empty() {
            return {};
        });
        // Only clear the local flag on a CONFIRMED discard. Treating any response as success (an
        // HTML error page, a 500) would make a retry skip the discard it still owes.
        if (response.ok && result && result.discarded === true) {
            context.temporaryBasket = false;
        }
    } catch (error) {
        if (win && win.console) {
            win.console.warn("[express:paypal] could not discard the temporary basket:", error);
        }
    }
}

/**
 * Creates the duck-typed component walletRenderer consumes: { isAvailable, mount }.
 *
 * @param {Object} options - factory options
 * @param {Object} options.deps - injected dependencies ({ doc, win, errorMessage })
 * @param {string} options.surface - "cart", "cart-sticky", "minicart" or "pdp"
 * @param {Object} options.container - the express container element (attribute source)
 * @returns {Promise<Object|null>} the component, or null when PayPal cannot be offered
 */
async function createComponent(options) {
    const deps = options.deps;
    const container = options.container;
    const win = deps && deps.win;

    const sdkReady = await ensureSdk(deps);
    if (!sdkReady || !win.paypalUrls || !win.paypalUrls.getPaypalOrderId || !win.paypalUrls.returnFromCart) {
        return null;
    }

    const urls = {
        getPaypalOrderId: win.paypalUrls.getPaypalOrderId,
        returnFromCart: win.paypalUrls.returnFromCart,
        placeOrder: container.getAttribute("data-express-place-order-url"),
        /*
         * PDP express buys ONE product, through a temporary basket.
         *
         * `createTemporaryBasket` is Adyen's endpoint, read off the window global that
         * adyen/expressPaymentsMetadata.isml already emits for the Adyen wallets — reused rather
         * than duplicated, because it is integration-agnostic (see createTemporaryBasketForPdp).
         * `discardTemporaryBasket` is ours, since Adyen exposes no equivalent: their create
         * endpoint only clears leftovers on its own next call.
         *
         * addToCart/removeItem/updateQuantity are gone with the real-basket add they served.
         */
        /*
         * Off the PayPal container, and ONLY there — deliberately no fallback to Adyen's
         * window.createTemporaryBasketUrl.
         *
         * Two reasons. That global is emitted by adyen/expressPaymentsMetadata.isml, which the PDP
         * template only includes when ADYEN pdp express is enabled, so relying on it made a hub
         * with PayPal PDP on and Adyen PDP off render a button that failed on every click — the
         * switches are independent site preferences. And our route
         * (Paypal-CreateExpressBasket) returns the flow id that correlates every later call;
         * Adyen's does not, so falling back to it would fail the create check anyway.
         */
        createTemporaryBasket: container.getAttribute("data-express-create-basket-url"),
        discardTemporaryBasket: container.getAttribute("data-express-discard-basket-url")
    };
    if (!urls.placeOrder) {
        return null;
    }

    const buttonConfig = readJsonAttribute(container, "data-paypal-button-config") || {};
    const billingFormFields = readJsonAttribute(container, "data-paypal-billing-form-fields") || {};
    const csrf = readCsrfToken(deps.doc);
    const context = {
        deps: deps,
        container: container,
        urls: urls,
        csrf: csrf,
        billingFormFields: billingFormFields
    };

    // Every refresh of a surface builds a fresh component; the SDK instance the previous render
    // left behind holds listeners and closures the DOM removal alone does not release —
    // int_paypal's own models close() before re-rendering for the same reason.
    if (container.sdPayPalExpressButtons && typeof container.sdPayPalExpressButtons.close === "function") {
        try {
            container.sdPayPalExpressButtons.close();
        } catch (error) {
            // an instance the SDK already tore down throws on close; there is nothing to release
        }
    }

    const buttons = win.paypal.Buttons({
        style: Object.assign({}, buttonConfig.style || {}, EXPRESS_BUTTON_STYLE),
        onClick: function onClick() {
            expressDom.setSurfaceError(container, "");
        },
        createOrder: async function createOrder() {
            if (options.surface === "pdp") {
                await createTemporaryBasketForPdp(context);
            }
            // isExpressWallet marks THIS surface specifically: the pre-existing checkout-page
            // PayPal button also sends isExpress:true, and the server needs to tell the two
            // apart — basket shipping-method normalisation must only run for the express wallet
            // surfaces, never for the checkout button (a Click&Collect basket must not be
            // silently rewritten there).
            const url = withParam(withFlow(withCsrf(urls.getPaypalOrderId, csrf), context), "paymentSourceData", JSON.stringify({ isExpress: true, isExpressWallet: true }));
            const response = await win.fetch(url, { method: "GET" });
            const result = await response.json();
            if (!result || result.error || !result.id) {
                // Follow the server-prepared error page when there is one; the throw still runs so
                // the SDK aborts its flow instead of opening a sheet for an order that failed.
                /*
                 * Routed through reportOrderFailure, exactly as completeOrder's failures are.
                 * REVIEW FINDING: this path still navigated, and on the PDP that lands the shopper
                 * on an empty bag with the message dropped — Checkout-Begin bails to Cart-Show when
                 * there is no basket, and PDP express deliberately leaves the real one untouched.
                 * Lower severity than the completeOrder case (the sheet has not opened, no payment
                 * has been approved, so they are bounced after TAPPING rather than after paying),
                 * but the same mechanism on the same surface two functions away, and the PDP's own
                 * error element is right there.
                 *
                 * reportOrderFailure keeps the discard-before-navigate ordering for the bag
                 * surfaces, which still matters: navigation cancels in-flight requests, and the
                 * marker must not survive — left set, the checkout-page PayPal button would resolve
                 * this temporary basket instead of the shopper's own.
                 */
                await reportOrderFailure(context, result);
                throw new Error((result && result.errorMessage) || "could not create the PayPal order");
            }
            // Remembered so onApprove can prove PayPal is approving the order THIS button created.
            context.paypalOrderId = result.id;
            return result.id;
        },
        onApprove: function onApprove(data) {
            /*
             * PayPal passes the approved order id. Verify it against the one we created rather than
             * ignoring the argument: an approval for a DIFFERENT order — a replayed callback, or a
             * second tab's sheet resolving into this page's handler — must not be completed against
             * this flow's basket. Only checked when both ids are known, so a PayPal payload without
             * orderID cannot block a legitimate approval.
             */
            // Strict: once we know which order we created, an approval must name it. Permitting a
            // MISSING orderID left the weaker case open, and the authoritative check is now
            // server-side anyway (expressBasket.flowOrderId, re-asserted in ReturnFromCart) — this
            // just stops a mismatched approval before it costs a round trip.
            if (context.paypalOrderId && (!data || data.orderID !== context.paypalOrderId)) {
                // Busy cleared explicitly: setSurfaceError only clears it when it has a message to show,
                // so an empty data-express-error-message would leave the spinner turning for good.
                expressDom.setSurfaceBusy(container, false);
                expressDom.setSurfaceError(container, deps.errorMessage || "");
                if (deps.win && deps.win.console) {
                    deps.win.console.error("[express:paypal] approval for a different order, ignoring");
                }
                return discardTemporaryBasket(context);
            }
            return completeOrder(context);
        },
        onCancel: function onCancel() {
            expressDom.setSurfaceBusy(container, false);
            // A closed sheet must not silently leave the product in the bag (user direction) —
            // the Adyen wallets' temporary basket never touches the real bag at all. The promise
            // is RETURNED so the SDK sequences it before any immediate retry's createOrder.
            if (options.surface === "pdp") {
                return discardTemporaryBasket(context);
            }
            return undefined;
        },
        onError: function onError(error) {
            // Busy cleared explicitly: setSurfaceError only clears it when it has a message to show,
            // so an empty data-express-error-message would leave the spinner turning for good.
            expressDom.setSurfaceBusy(container, false);
            expressDom.setSurfaceError(container, deps.errorMessage || "");
            if (win.console) {
                win.console.error("[express:paypal]", error);
            }
            /*
             * Discard on EVERY pdp error, navigating or not. The old carve-out existed because the
             * product had been added to the real bag and had to survive the trip to checkout;
             * nothing is added now, so there is nothing to preserve and a surviving marker is
             * actively harmful — the checkout-page PayPal button would resolve this temporary
             * basket rather than the shopper's own.
             */
            if (options.surface === "pdp") {
                return discardTemporaryBasket(context);
            }
            return undefined;
        }
    });

    container.sdPayPalExpressButtons = buttons;

    return {
        isAvailable: function isAvailable() {
            try {
                return buttons.isEligible();
            } catch (error) {
                return false;
            }
        },
        mount: function mount(element) {
            return buttons.render(element);
        }
    };
}

module.exports = {
    createComponent: createComponent,
    // exposed for tests
    ensureSdk: ensureSdk,
    buildReturnBody: buildReturnBody,
    readCsrfToken: readCsrfToken,
    withCsrf: withCsrf,
    withParam: withParam
};
